/* global document, setTimeout */
/**
 * webOpenInApp.js
 *
 * WEB BUILD ONLY (services.happysolutions.hu). When this web app is opened in
 * a phone browser, hand the visitor off to the installed native app once per
 * tab — same contract as the admin repo's `src/pwa/openInAppPrompt.js`.
 *
 * Universal Links / App Links are never honoured for a typed-in URL or a link
 * to the domain the browser is already on, so the hand-off goes through the
 * app's custom scheme and carries the full page URL along:
 *   - iOS:     `happy-app://open?url=<encoded href>`
 *   - Android: `intent://open?url=…#Intent;scheme=happy-app;package=…;S.browser_fallback_url=…;end`
 *              (no app installed → Chrome silently stays on this page).
 * The native side unwraps it in `adminLinkResolver.js` (`unwrapOpenInAppUrl`).
 *
 * iOS only auto-launches after a previous launch proved the app is installed
 * (the page went hidden right after): without the app Safari shows an
 * "invalid address" alert instead of a silent fallback.
 *
 * When no auto-launch happens, or the page is still visible after it (no app,
 * or Chrome refused a launch without a tap), the top download bar
 * (`appDownloadBanner.js`) offers the store link, or "Open" once the app is
 * known to be installed. iOS Safari gets Apple's native banner instead.
 * The older bottom toast (`showOpenInAppToast`) is kept for explicit callers.
 * ✕ = stay on web for a week (auto-launch included).
 *
 * Dependency-free (no React Native imports) so it is unit-testable in Jest.
 */

import {getMobilePlatform, isIOSSafari, showAppDownloadBanner} from './appDownloadBanner';

export const ANDROID_PACKAGE = 'org.happysolutions.app';
export const APP_SCHEME = 'happy-app';

const AUTO_ATTEMPT_SESSION_KEY = 'happy:openInAppAutoAttempted';
const APP_INSTALLED_STORAGE_KEY = 'happy:appInstalledSeenAt';
const LAUNCH_DETECT_MS = 2500;
const DISMISS_STORAGE_KEY = 'happy:openInAppDismissedAt';
const DISMISS_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000;
const TOAST_ID = 'happy-open-in-app';
const IOS_STORE_URL = 'https://apps.apple.com/hu/app/id6782328664';
const ANDROID_STORE_URL = `https://play.google.com/store/apps/details?id=${ANDROID_PACKAGE}`;

// OAuth / magic-link callbacks consume a one-time secret on load — handing
// those URLs to the app would break the web sign-in (social-login popups land
// back on this host with `?code=` / `#access_token=`).
const CALLBACK_QUERY_RE = /[?&](code|state|token|access_token|id_token|oauth_token)=/i;
const CALLBACK_HASH_RE = /(^#|[#&])(token|access_token|id_token)=/i;

const userAgent = () => (typeof navigator !== 'undefined' && navigator.userAgent) || '';
const isIOS = () => /iphone|ipad|ipod/i.test(userAgent());
const isAndroid = () => /android/i.test(userAgent());

/**
 * Whether `href` may be handed to the app at all.
 * @param {string} href absolute page URL
 * @returns {boolean} false for auth callbacks and `?web=1` (explicit "stay on web").
 */
export const isAppLinkableUrl = (href) => {
    const match = /^https?:\/\/[^/?#]*([^?#]*)(\?[^#]*)?(#.*)?$/i.exec(String(href || ''));

    if (!match) {
        return false;
    }

    const [, , query = '', hash = ''] = match;

    if (/[?&]web=1(&|$)/.test(query)) {
        return false;
    }

    return !CALLBACK_QUERY_RE.test(query) && !CALLBACK_HASH_RE.test(hash);
};

/**
 * The URL that launches the native app on `href`'s screen.
 * @param {string} href absolute page URL to open in the app
 * @param {{android?: boolean}} [options] `android: true` → Chrome `intent://` URL with a silent same-page fallback
 * @returns {string}
 */
export const buildAppLaunchUrl = (href, {android = false} = {}) => {
    const encoded = encodeURIComponent(href);

    if (!android) {
        return `${APP_SCHEME}://open?url=${encoded}`;
    }

    return `intent://open?url=${encoded}#Intent;scheme=${APP_SCHEME};package=${ANDROID_PACKAGE};S.browser_fallback_url=${encoded};end`;
};

const readStorage = (storage, key) => {
    try {
        return window[storage].getItem(key);
    } catch (_error) {
        return null;
    }
};

const writeStorage = (storage, key, value) => {
    try {
        window[storage].setItem(key, value);
    } catch (_error) {
        // Storage unavailable (private browsing) — worst case the hand-off is
        // retried / not remembered, never a crash.
    }
};

/**
 * Navigate to the app hand-off URL and remember the app as installed when the
 * page goes to the background right after (= the app took over).
 * @returns {void}
 */
const launchApp = () => {
    const onHidden = () => {
        if (document.visibilityState === 'hidden') {
            writeStorage('localStorage', APP_INSTALLED_STORAGE_KEY, String(Date.now()));
        }
    };

    document.addEventListener('visibilitychange', onHidden);
    setTimeout(() => document.removeEventListener('visibilitychange', onHidden), LAUNCH_DETECT_MS);
    window.location.href = buildAppLaunchUrl(window.location.href, {android: isAndroid()});
};

const escapeHtml = (value) => String(value).replace(/[&<>"']/g, (ch) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
}[ch]));

/**
 * Show the dismissible "Open in the Happy app" toast (once per page).
 * @param {(key: string, fallback: string) => string} translate i18n lookup with a Hungarian fallback
 * @returns {void}
 */
export const showOpenInAppToast = (translate) => {
    if (document.getElementById(TOAST_ID) || !document.body) {
        return;
    }

    const t = (key, fallback) => {
        try {
            const value = translate ? translate(key, fallback) : fallback;

            return value && value !== key ? value : fallback;
        } catch (_error) {
            return fallback;
        }
    };

    const storeUrl = isIOS() ? IOS_STORE_URL : ANDROID_STORE_URL;
    const storeLabel = isIOS()
        ? t('open_in_app_get_ios', 'App Store-ból letölthető')
        : t('open_in_app_get_android', 'Google Play-en elérhető');

    const toast = document.createElement('div');
    toast.id = TOAST_ID;
    toast.setAttribute('role', 'dialog');
    toast.style.cssText = [
        'position:fixed', 'left:12px', 'right:12px', 'bottom:calc(12px + env(safe-area-inset-bottom, 0px))',
        'z-index:2147483000', 'display:flex', 'gap:12px', 'align-items:flex-start', 'padding:14px 40px 14px 14px',
        'background:#fff', 'color:#1f2937', 'border-radius:14px', 'box-shadow:0 10px 30px rgba(15,23,42,.18)',
        'font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif', 'max-width:480px', 'margin:0 auto',
    ].join(';');
    toast.innerHTML = `
<div style="flex:0 0 40px;height:40px;border-radius:50%;background:#F97316;display:flex;align-items:center;justify-content:center">
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true"><rect x="6.5" y="2.5" width="11" height="19" rx="2.4" stroke="#fff" stroke-width="2"/><path d="M12 18.15h.01" stroke="#fff" stroke-width="2.4" stroke-linecap="round"/></svg>
</div>
<div style="flex:1;min-width:0">
    <div style="font-weight:700;font-size:15px;line-height:1.3">${escapeHtml(t('open_in_app_title', 'Nyissa meg a Happy appban'))}</div>
    <div style="font-size:13px;line-height:1.4;color:#6b7280;margin-top:2px">${escapeHtml(t('open_in_app_text', 'A teljes élményért használja mobilalkalmazásunkat.'))}</div>
    <div style="display:flex;flex-wrap:wrap;align-items:center;gap:12px;margin-top:10px">
        <a href="#" data-happy-open-in-app="open" style="background:#F97316;color:#fff;font-weight:600;font-size:14px;padding:8px 16px;border-radius:10px;text-decoration:none">${escapeHtml(t('open_in_app_open', 'Megnyitás appban'))}</a>
        <a href="${storeUrl}" target="_blank" rel="noopener noreferrer" style="color:#F97316;font-size:13px;text-decoration:none">${escapeHtml(storeLabel)}</a>
    </div>
</div>
<button type="button" data-happy-open-in-app="later" aria-label="${escapeHtml(t('open_in_app_dismiss', 'Elvetés'))}" style="position:absolute;top:6px;right:8px;border:0;background:transparent;font-size:22px;line-height:1;color:#9ca3af;padding:6px;cursor:pointer">&times;</button>`;

    toast.querySelector('[data-happy-open-in-app="open"]').addEventListener('click', (event) => {
        // A real tap — Chrome allows the intent here even where the load-time
        // auto-launch was refused.
        event.preventDefault();
        toast.remove();
        launchApp();
    });
    toast.querySelector('[data-happy-open-in-app="later"]').addEventListener('click', () => {
        writeStorage('localStorage', DISMISS_STORAGE_KEY, String(Date.now()));
        toast.remove();
    });

    document.body.appendChild(toast);
};

/**
 * Top "Happy App – Smart Business" download bar (appDownloadBanner.js), the
 * default offer since it replaced the toast. Skipped on iOS Safari, which shows
 * Apple's native Smart App Banner from web/index.html. ✕ = stay on web for a week.
 * @param {(key: string, fallback: string) => string} [translate] i18n lookup
 * @returns {void}
 */
const showDownloadBar = (translate) => {
    if (isIOSSafari(userAgent())) {
        return;
    }

    showAppDownloadBanner({
        platform: getMobilePlatform(userAgent()),
        translate,
        keys: {
            storeIos: 'open_in_app_get_ios',
            storeAndroid: 'open_in_app_get_android',
            get: 'open_in_app_bar_get',
            open: 'open_in_app_bar_open',
            dismiss: 'open_in_app_dismiss',
        },
        // The services web build serves no PNG icon; admin hosts the same brand icon.
        iconUrl: 'https://admin.happysolutions.hu/favicon/web-app-manifest-192x192.png',
        source: 'services_web',
        appInstalled: !!readStorage('localStorage', APP_INSTALLED_STORAGE_KEY),
        onOpen: launchApp,
        onDismiss: () => writeStorage('localStorage', DISMISS_STORAGE_KEY, String(Date.now())),
    });
};

/**
 * Web-boot entry: hand the visitor off to the native app (once per tab), or
 * offer the download bar. No-op outside a mobile browser, inside an installed PWA,
 * in an OAuth popup (`window.opener`), on callback URLs, or within the ✕
 * cooldown. Never throws — the web app must boot regardless.
 * @param {(key: string, fallback: string) => string} [translate] i18n lookup for the toast
 * @returns {boolean} true when an auto-launch was attempted.
 */
export const maybeOpenNativeApp = (translate) => {
    try {
        if (typeof window === 'undefined' || typeof document === 'undefined' || !(isIOS() || isAndroid())) {
            return false;
        }

        const standalone = window.navigator?.standalone === true
            || (typeof window.matchMedia === 'function' && window.matchMedia('(display-mode: standalone)').matches);

        if (standalone || window.opener || window.ReactNativeWebView || !isAppLinkableUrl(window.location.href)) {
            return false;
        }

        if (Date.now() - (Number(readStorage('localStorage', DISMISS_STORAGE_KEY)) || 0) < DISMISS_COOLDOWN_MS) {
            return false;
        }

        const canAutoLaunch = !readStorage('sessionStorage', AUTO_ATTEMPT_SESSION_KEY)
            && !(isIOS() && !readStorage('localStorage', APP_INSTALLED_STORAGE_KEY));

        // Download bar after the launch window (still here = no app / launch
        // refused), or once the web app had a moment to render when no launch happens.
        setTimeout(() => {
            try {
                if (document.visibilityState === 'visible') {
                    showDownloadBar(translate);
                }
            } catch (_error) {
                // cosmetic only
            }
        }, LAUNCH_DETECT_MS);

        if (!canAutoLaunch) {
            return false;
        }

        writeStorage('sessionStorage', AUTO_ATTEMPT_SESSION_KEY, String(Date.now()));
        launchApp();

        return true;
    } catch (_error) {
        // Never let the hand-off break the web app's boot.
        return false;
    }
};

export default maybeOpenNativeApp;
